import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { tokenStorage } from '../services/auth';
import { useAuth } from '../contexts/AuthContext';

/**
 * Landing point for the GitHub OAuth redirect.
 *
 * The backend puts the tokens in the URL *fragment* rather than the query
 * string: fragments are never sent to a server, so the tokens stay out of
 * proxy and access logs. We read them once, wipe the hash from the address bar
 * and from history, then hand off to the normal auth flow.
 */
export default function GithubCallbackPage() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [error, setError] = useState<string | null>(null);
  // StrictMode double-invokes effects in dev; without this the hash is cleared
  // by the first run and the second finds nothing.
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const next = params.get('next') || '/dashboard';

    // Remove the tokens from the address bar and from the history entry before
    // anything else runs, so a back-navigation or a shared URL cannot leak them.
    window.history.replaceState(null, '', '/auth/github');

    if (!accessToken || !refreshToken) {
      setError('Sign-in did not return a session. Please try again.');
      const t = setTimeout(() => navigate('/login', { replace: true }), 2500);
      return () => clearTimeout(t);
    }

    tokenStorage.setTokens(accessToken, refreshToken);
    refreshUser()
      .then(() => navigate(next.startsWith('/') ? next : '/dashboard', { replace: true }))
      .catch(() => {
        tokenStorage.clearTokens();
        setError('Could not load your account. Please sign in again.');
        setTimeout(() => navigate('/login', { replace: true }), 2500);
      });
  }, [navigate, refreshUser]);

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        {error ? (
          <p className="text-destructive">{error}</p>
        ) : (
          <>
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-muted-foreground" />
            <p className="mt-4 text-muted-foreground">Completing GitHub sign-in…</p>
          </>
        )}
      </div>
    </div>
  );
}
